/**
 * Notification enums and preference shape - SOURCE OF TRUTH
 * Pure TypeScript - no Drizzle imports
 */
// Notification type labels
export const notificationTypeLabels = {
    appointment_booked: 'Appointment booked',
    appointment_rescheduled: 'Appointment rescheduled',
    appointment_cancelled: 'Appointment cancelled',
    chatbot_handoff: 'Conversation needs you',
    // Claire keeps the conversation and told the customer the team will send
    // something the clinic never set up (a price, a treatment's details).
    chatbot_needs_info: 'Claire needs a detail from you',
    ad_rejected: 'Ad rejected',
    lead_created: 'New lead',
    integration_disconnected: 'Connection needs attention',
    phone_call_missed: 'Missed call',
    ads_setup_step: 'Next step for your ads',
    ads_running_after_cancellation: 'Ads still running',
};
export const notificationTypeValues = Object.keys(notificationTypeLabels);
// --- Notification preference shape (persisted as a JSON column) ---
// Scope controls whose events a user is notified about.
export const notificationScopeLabels = {
    mine: 'Only mine',
    all: 'Everyone',
    off: 'Off',
};
export const notificationScopeValues = Object.keys(notificationScopeLabels);
// Notification preference categories — the JSON keys.
export const notificationCategoryLabels = {
    appointments: 'Appointments',
    inbox: 'Inbox handoffs',
    advertising: 'Ads: rejections and setup steps',
    leads: 'New leads',
    integrations: 'Connection problems',
};
// Applied to every new notification_preference row and merged over partial
// JSON on read, so adding a category later never needs a migration.
export const defaultNotificationPreferences = {
    appointments: {
        scope: 'all',
        channels: { email: true, push: true, whatsapp: false },
    },
    // Handoffs can be unassigned, so every member gets email and push by default.
    inbox: {
        scope: 'all',
        channels: { email: true, push: true, whatsapp: false },
    },
    advertising: {
        enabled: true,
        channels: { email: true, push: true, whatsapp: false },
    },
    // Defaults to 'all' because most leads arrive unowned (Meta instant forms,
    // chatbot), so 'mine' would silently notify nobody in the common case.
    // Email is off — lead email notifications are deliberately out of scope.
    leads: {
        scope: 'all',
        channels: { email: false, push: true, whatsapp: false },
    },
    // Org-wide: a dead Facebook Page belongs to the business, not to a person.
    // Email defaults on and, per CRITICAL_NOTIFICATION_TYPES below, cannot be
    // turned off — see the comment there for why.
    integrations: {
        enabled: true,
        channels: { email: true, push: true, whatsapp: false },
    },
};
/**
 * Merges stored (possibly partial or older-shape) preference JSON over the
 * current defaults, so a row written before a new category existed still
 * yields a complete object — no migration needed when the shape grows.
 */
export const withPreferenceDefaults = (stored) => {
    const d = defaultNotificationPreferences;
    return {
        appointments: {
            scope: stored?.appointments?.scope ?? d.appointments.scope,
            channels: {
                email: stored?.appointments?.channels?.email ??
                    d.appointments.channels.email,
                push: stored?.appointments?.channels?.push ?? d.appointments.channels.push,
                whatsapp: stored?.appointments?.channels?.whatsapp ??
                    d.appointments.channels.whatsapp,
            },
        },
        inbox: {
            scope: stored?.inbox?.scope ?? d.inbox.scope,
            channels: {
                email: stored?.inbox?.channels?.email ?? d.inbox.channels.email,
                push: stored?.inbox?.channels?.push ?? d.inbox.channels.push,
                whatsapp: stored?.inbox?.channels?.whatsapp ?? d.inbox.channels.whatsapp,
            },
        },
        advertising: {
            enabled: stored?.advertising?.enabled ?? d.advertising.enabled,
            channels: {
                email: stored?.advertising?.channels?.email ?? d.advertising.channels.email,
                push: stored?.advertising?.channels?.push ?? d.advertising.channels.push,
                whatsapp: stored?.advertising?.channels?.whatsapp ??
                    d.advertising.channels.whatsapp,
            },
        },
        leads: {
            scope: stored?.leads?.scope ?? d.leads.scope,
            channels: {
                email: stored?.leads?.channels?.email ?? d.leads.channels.email,
                push: stored?.leads?.channels?.push ?? d.leads.channels.push,
                whatsapp: stored?.leads?.channels?.whatsapp ?? d.leads.channels.whatsapp,
            },
        },
        integrations: {
            enabled: stored?.integrations?.enabled ?? d.integrations.enabled,
            channels: {
                email: stored?.integrations?.channels?.email ??
                    d.integrations.channels.email,
                push: stored?.integrations?.channels?.push ?? d.integrations.channels.push,
                whatsapp: stored?.integrations?.channels?.whatsapp ??
                    d.integrations.channels.whatsapp,
            },
        },
    };
};
/**
 * Types that reach the customer regardless of their preferences.
 *
 * A muted "your Instagram stopped replying" is just another silent blackout,
 * which is the exact failure being fixed: the 2026-08-24 audit found
 * connections dead for over two months with nobody told. So the in-app row and
 * the email always go.
 *
 * Push still respects the preference — it is the intrusive channel, and someone
 * who has turned push off has made a reasonable choice about their phone rather
 * than about whether they want to know.
 */
export const CRITICAL_NOTIFICATION_TYPES = new Set([
    'integration_disconnected',
    // A cancelled clinic whose Borradh-launched ads still spend their money.
    // Muting it would leave them paying for ads nobody manages.
    'ads_running_after_cancellation',
]);
//# sourceMappingURL=notifications.js.map